import React, { useState, useCallback, useRef, createContext, useContext } from 'react';
import useVideoContext from '../useVideoContext/useVideoContext';
import { debounce } from '@alucio/lux-ui';
export const ScreenShareToggleContext = createContext(null);
export function ScreenShareToggleProvider(props) {
    const { room, onError } = useVideoContext();
    const [isSharing, setIsSharing] = useState(false);
    const isRequestingPermissions = useRef(false);
    const stopScreenShareRef = useRef(null);
    const shareScreen = useCallback(debounce((cb) => {
        isRequestingPermissions.current = true;
        navigator.mediaDevices
            .getDisplayMedia({
            audio: false,
            video: {
                frameRate: 10,
                height: 1080,
                width: 1920,
            },
        })
            .then(stream => {
            const track = stream.getTracks()[0];
            setIsSharing(true);
            // All video tracks are published with 'low' priority. This works because the video
            // track that is displayed in the 'MainParticipant' component will have it's priority
            // set to 'high' via track.setPriority()
            const publishTrack = () => {
                room.localParticipant
                    .publishTrack(track, {
                    name: 'screen',
                    priority: 'low',
                })
                    .then(trackPublication => {
                    stopScreenShareRef.current = () => {
                        room.localParticipant.unpublishTrack(track);
                        // TODO: remove this if the SDK is updated to emit this event
                        room.localParticipant.emit('trackUnpublished', trackPublication);
                        track.stop();
                        setIsSharing(false);
                    };
                    track.onended = stopScreenShareRef.current;
                })
                    .catch(onError);
            };
            cb ? cb(publishTrack) : publishTrack();
        })
            .catch(error => {
            // Don't display an error if the user closes the screen share dialog
            if (error.name !== 'AbortError' && error.name !== 'NotAllowedError') {
                onError(error);
            }
        }).finally(() => {
            isRequestingPermissions.current = false;
        });
    }, 500), [room, onError]);
    const stopSharing = () => stopScreenShareRef && stopScreenShareRef.current && stopScreenShareRef.current();
    const startSharing = (cb) => !isRequestingPermissions.current && shareScreen(cb);
    const toggleScreenShare = useCallback(() => {
        !isSharing ? shareScreen() : stopScreenShareRef.current();
    }, [isSharing, shareScreen, stopScreenShareRef]);
    return (React.createElement(ScreenShareToggleContext.Provider, { value: { isSharing, toggleScreenShare, startSharing, stopSharing } }, props.children));
}
export default function useScreenShareToggle() {
    const { isSharing, toggleScreenShare, startSharing, stopSharing } = useContext(ScreenShareToggleContext);
    return [isSharing, toggleScreenShare, startSharing, stopSharing];
}
//# sourceMappingURL=useScreenShareToggle.js.map